/**
 * Parent-side postMessage bridge for the checkout iframe.
 *
 * Mirrors the protocol defined in source/app/bridge/postMessage.ts. Inbound
 * messages from the checkout iframe origin are validated by origin + envelope
 * shape (`source: 'salesbyte-checkout'`, `v: 1`) before any handler runs.
 */

export const PROTOCOL_VERSION = 1;
export const PROTOCOL_SOURCE = "salesbyte-checkout";

export function isValidEnvelope(data) {
    return (
        data &&
        typeof data === "object" &&
        data.source === PROTOCOL_SOURCE &&
        data.v === PROTOCOL_VERSION &&
        typeof data.type === "string"
    );
}

/**
 * @param {{ checkoutOrigin: string, getIframeWindow: () => Window | null, onMessage: (msg: { type: string, payload: any }) => void }} opts
 */
export function createCheckoutIframeBridge({ checkoutOrigin, getIframeWindow, onMessage }) {
    if (!checkoutOrigin) {
        throw new Error("checkoutIframeBridge: checkoutOrigin is required");
    }

    const listener = (event) => {
        if (event.origin !== checkoutOrigin) return;
        const iframeWindow = getIframeWindow();
        if (iframeWindow && event.source !== iframeWindow) return;
        if (!isValidEnvelope(event.data)) return;

        onMessage(event.data);
    };

    window.addEventListener("message", listener);

    return {
        send(type, payload) {
            const target = getIframeWindow();
            if (!target) return false;
            target.postMessage(
                { v: PROTOCOL_VERSION, source: PROTOCOL_SOURCE, type, payload: payload || {} },
                checkoutOrigin
            );
            return true;
        },
        destroy() {
            window.removeEventListener("message", listener);
        },
    };
}
